import { formatDate } from '@longy/common'
import * as df from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useMemo, useState } from 'react'

import { cn } from '../../lib/utils'
import { ButtonWithIcon } from '../molecules'
import { ScrollArea, ScrollBar } from '../ui'

export type TimeSlotCalendarProps = React.HTMLAttributes<HTMLDivElement> & {
    availableTimeSlots?: {
        id: string
        date: Date
        endDate?: Date
        durationMinutes?: number
        label?: string
    }[]
    bookedSlotIds?: string[]
    selectedSlotId?: string
    onSelectedSlotChange?: (slotId: string | undefined) => void
    fromHour?: number
    toHour?: number
    minutesInterval?: number
    showUnavailableTimeSlots?: boolean
    startDate?: Date
}

export const TimeSlotCalendar = ({
    className,
    availableTimeSlots = [],
    bookedSlotIds = [],
    selectedSlotId,
    onSelectedSlotChange,
    showUnavailableTimeSlots,
    startDate = new Date(),
    ...props
}: TimeSlotCalendarProps) => {
    const [currentWeekStart, setCurrentWeekStart] = useState(
        df.startOfWeek(startDate, { weekStartsOn: 1 }),
    )

    const currentWeekEnd = useMemo(
        () => df.endOfWeek(currentWeekStart, { weekStartsOn: 1 }),
        [currentWeekStart],
    )

    const days = useMemo(
        () => Array.from({ length: 7 }, (_, i) => df.addDays(currentWeekStart, i)),
        [currentWeekStart],
    )

    const isSelected = (slotId: string) => selectedSlotId === slotId

    const isBooked = (slotId: string) => bookedSlotIds.includes(slotId)

    const spanningSlots = useMemo(
        () =>
            availableTimeSlots
                .filter((slot) => {
                    const endDate =
                        slot.endDate ?? df.addMinutes(slot.date, slot.durationMinutes ?? 30)
                    return !df.isSameDay(slot.date, endDate)
                })
                .sort((a, b) => {
                    const aEnd = a.endDate ?? df.addMinutes(a.date, a.durationMinutes ?? 30)
                    const bEnd = b.endDate ?? df.addMinutes(b.date, b.durationMinutes ?? 30)
                    const aDuration = aEnd.getTime() - a.date.getTime()
                    const bDuration = bEnd.getTime() - b.date.getTime()
                    return bDuration - aDuration
                }),
        [availableTimeSlots],
    )

    const singleDaySlots = useMemo(
        () =>
            availableTimeSlots.filter((slot) => {
                const endDate = slot.endDate ?? df.addMinutes(slot.date, slot.durationMinutes ?? 30)
                return df.isSameDay(slot.date, endDate)
            }),
        [availableTimeSlots],
    )

    // Calculate dynamic height based on duration (1.5px per minute, minimum 45px for 30min slots)
    const calculateSlotHeight = (durationMinutes?: number) => {
        if (!durationMinutes) return 45 // default height for 30min slots
        return Math.max(durationMinutes * 1.5, 45)
    }

    // Find the earliest and latest times across single-day slots to create a time grid
    const timeRange = useMemo(() => {
        if (singleDaySlots.length === 0) return { earliest: 0, latest: 24 * 60 }

        const times = singleDaySlots.map((slot) => {
            const hours = df.getHours(slot.date)
            const minutes = df.getMinutes(slot.date)
            return hours * 60 + minutes
        })

        const earliest = Math.min(...times)
        const latest = Math.max(...times) + 120 // Add 2 hours buffer for slot duration

        return { earliest, latest }
    }, [singleDaySlots])

    // Calculate position from top based on time (1.5px per minute)
    const calculateTopPosition = (date: Date) => {
        const hours = df.getHours(date)
        const minutes = df.getMinutes(date)
        const totalMinutes = hours * 60 + minutes
        const offsetFromEarliest = totalMinutes - timeRange.earliest
        return offsetFromEarliest * 1.5
    }

    const calculateUnavailableSlots = (day: Date) => {
        const allSlots = []
        let current = df.startOfDay(day)
        while (current < df.endOfDay(day)) {
            allSlots.push(current)
            current = df.addMinutes(current, 30)
        }

        return allSlots.filter(
            (slot) =>
                !availableTimeSlots.some((availableSlot) => df.isEqual(slot, availableSlot.date)),
        )
    }

    return (
        <div {...props} className={cn('space-y-3 border rounded-md', className)}>
            <div className="flex items-center justify-between p-2">
                <ButtonWithIcon
                    icon={ChevronLeft}
                    onClick={() => setCurrentWeekStart((prev) => df.subWeeks(prev, 1))}
                    className="rounded-md"
                />
                <div>
                    {formatDate(currentWeekStart, 'MMMM dd.')}-{' '}
                    {formatDate(currentWeekEnd, 'MMMM dd.')}
                </div>
                <ButtonWithIcon
                    icon={ChevronRight}
                    onClick={() => setCurrentWeekStart((prev) => df.addWeeks(prev, 1))}
                    className="rounded-md"
                />
            </div>
            <div className="flex items-center justify-around bg-[#F9F9F9]">
                {days.map((day, i) => (
                    <div key={i}>
                        <div className="space-y-3 p-3">
                            <div className="text-center">{formatDate(day, 'EEE')}</div>
                            <div className="text-center text-[#747474]">
                                {formatDate(day, 'MM.dd.')}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {spanningSlots.length > 0 && (
                <div className="flex items-start justify-around border-b px-1 py-2">
                    {days.map((day, i) => {
                        const daySpanningSlots = spanningSlots.filter((slot) => {
                            const end =
                                slot.endDate ?? df.addMinutes(slot.date, slot.durationMinutes ?? 30)
                            return slot.date <= df.endOfDay(day) && end >= df.startOfDay(day)
                        })

                        return (
                            <div key={i} className="w-full px-1">
                                {daySpanningSlots.map((slot) => {
                                    const endDate =
                                        slot.endDate ??
                                        df.addMinutes(slot.date, slot.durationMinutes ?? 30)
                                    const slotSelected = isSelected(slot.id)
                                    const slotBooked = isBooked(slot.id)
                                    return (
                                        <div
                                            key={slot.id}
                                            role="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (slotBooked) return
                                                onSelectedSlotChange?.(
                                                    slotSelected ? undefined : slot.id,
                                                )
                                            }}
                                            className={cn(
                                                'mb-1 text-center p-2 bg-[#6764FF]/15 rounded-md text-xs cursor-pointer',
                                                slotSelected && 'bg-primary text-white',
                                                slotBooked &&
                                                    'bg-gray-400 text-white cursor-not-allowed',
                                            )}
                                        >
                                            <div>{formatDate(slot.date, 'MM.dd. HH:mm')}</div>
                                            <div>{formatDate(endDate, 'MM.dd. HH:mm')}</div>
                                            {slot.label && (
                                                <div className="mt-1 font-semibold">
                                                    {slot.label}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            )}
            <ScrollArea className="h-[400px]">
                <div className="flex items-start justify-around">
                    {days.map((day, i) => {
                        const daySlots = singleDaySlots.filter((slot) =>
                            df.isSameDay(slot.date, day),
                        )
                        const containerHeight =
                            daySlots.length > 0 ? (timeRange.latest - timeRange.earliest) * 1.5 : 0

                        return (
                            <div
                                key={i}
                                className="w-full px-2 relative"
                                style={{ minHeight: `${containerHeight}px` }}
                            >
                                {daySlots.map((timeSlot) => {
                                    const slotHeight = calculateSlotHeight(timeSlot.durationMinutes)
                                    const topPosition = calculateTopPosition(timeSlot.date)
                                    return (
                                        <div
                                            onClick={() => {
                                                if (isBooked(timeSlot.id)) return
                                                onSelectedSlotChange?.(
                                                    isSelected(timeSlot.id)
                                                        ? undefined
                                                        : timeSlot.id,
                                                )
                                            }}
                                            role="button"
                                            key={timeSlot.id}
                                            style={{
                                                height: `${slotHeight}px`,
                                                top: `${topPosition}px`,
                                                position: 'absolute',
                                                left: '0.5rem',
                                                right: '0.5rem',
                                            }}
                                            className={cn(
                                                'text-center p-3 bg-[#6764FF]/15 rounded-md flex flex-col items-center justify-center',
                                                isSelected(timeSlot.id) && 'bg-primary text-white',
                                                isBooked(timeSlot.id) &&
                                                    'bg-gray-400 text-white cursor-not-allowed',
                                            )}
                                        >
                                            <span>{formatDate(timeSlot.date, 'HH:mm')}</span>
                                            {timeSlot.label && (
                                                <span className="mt-1 text-xs font-semibold">
                                                    {timeSlot.label}
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}
                                {showUnavailableTimeSlots &&
                                    calculateUnavailableSlots(day).map((timeSlot, i) => (
                                        <div
                                            key={i}
                                            className="text-center p-3 bg-[#F9F9F9] rounded-md h-[45px]"
                                        >
                                            {formatDate(timeSlot, 'HH:mm')}
                                        </div>
                                    ))}
                            </div>
                        )
                    })}
                </div>
                <ScrollBar />
            </ScrollArea>
        </div>
    )
}
